import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { Checkbox } from "../../../components/ui/checkbox";
import { Badge } from "../../../components/ui/badge";
import { useImportPreview, useUpdateImportPreview, usePublishImport } from "../../../lib/workDocumentImportQueries";
import { ImportPreviewBlockRow } from "./ImportPreviewBlockRow";

/**
 * Master v5 Prompt 30C Phase 2 — the coach's import preview, editing and
 * publish screen. Built entirely from the authoritative temporary draft
 * returned by the Phase 1 endpoints — never a fabricated preview.
 */
export default function ImportPreviewDialog({ open, onOpenChange, workspaceId, importId, initialDraft, members, isTeamWorkspace, onPublished }) {
  const navigate = useNavigate();
  const { data: draft } = useImportPreview(workspaceId, importId, { initialData: initialDraft });
  const updatePreview = useUpdateImportPreview(workspaceId, importId);
  const publish = usePublishImport(workspaceId, importId);

  const [title, setTitle] = useState(initialDraft?.title || "");
  const [assignmentMode, setAssignmentMode] = useState(isTeamWorkspace ? null : "personal");
  const [selectedMemberIds, setSelectedMemberIds] = useState(!isTeamWorkspace && members?.[0] ? [members[0].member_id] : []);
  const [publishError, setPublishError] = useState(null);
  const [publishedResult, setPublishedResult] = useState(null);

  useEffect(() => {
    if (draft?.title != null) setTitle(draft.title);
  }, [draft?.title]);

  if (!draft) return null;

  const blocks = draft.blocks || [];
  const hasBlockingErrors = draft.status === "invalid";
  const includedCount = blocks.filter((b) => !b.excluded).length;
  const canPublish = !hasBlockingErrors && includedCount > 0 && !!assignmentMode && (assignmentMode === "shared" || selectedMemberIds.length > 0);

  function sendOverride(importKey, partialFields) {
    const block = blocks.find((b) => b.import_key === importKey);
    const full = {
      ...(block.kind === "input" ? { label: block.label, help_text: block.help_text, input_type: block.input_type } : {}),
      style: block.style,
      excluded: block.excluded,
      ...partialFields,
    };
    updatePreview.mutate({ block_overrides: { [importKey]: full } });
  }

  function handleMove(index, direction) {
    const order = blocks.map((b) => b.import_key);
    const target = index + direction;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target], order[index]];
    updatePreview.mutate({ block_order: order });
  }

  function handleTitleBlur() {
    if (title !== draft.title) updatePreview.mutate({ title });
  }

  function toggleMember(memberId) {
    setSelectedMemberIds((prev) => (prev.includes(memberId) ? prev.filter((m) => m !== memberId) : [...prev, memberId]));
  }

  async function handlePublish() {
    setPublishError(null);
    try {
      const result = await publish.mutateAsync({
        title,
        assignment: assignmentMode === "shared" ? { mode: "shared" } : { mode: "personal", member_ids: selectedMemberIds },
      });
      toast.success("Published! The document is now live.");
      onPublished && onPublished(result);
      if (result.documents.length === 1) {
        // Exactly one document was created — no ambiguity about which
        // one the coach wants, so skip the intermediate "success"
        // screen and take them straight there.
        onOpenChange(false);
        navigate(`/rehearsal-room/w/${workspaceId}/work-documents/${result.documents[0].wdoc_id}`);
      } else {
        // Personal + multiple members creates several documents at
        // once — there's no single canonical one to jump to, so show
        // a pick-one screen instead of guessing.
        setPublishedResult(result);
      }
    } catch (e) {
      setPublishError(e.body?.detail?.message || e.body?.detail || e.message || "Publishing failed. It's safe to try again.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-ink">Interactive Blocks Import Preview</DialogTitle>
          <DialogDescription>
            Publishing this import creates a new interactive document. It will not update an existing document.
          </DialogDescription>
        </DialogHeader>

        {publishedResult ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="import-preview-published-success">
            <CheckCircle2 className="h-10 w-10 text-terracotta" />
            <p className="text-ink font-medium">Published {publishedResult.documents.length} documents — one per member</p>
            <div className="flex flex-col gap-1.5 w-full max-w-xs">
              {publishedResult.documents.map((d) => (
                <Button
                  key={d.wdoc_id} variant="outline"
                  onClick={() => { onOpenChange(false); navigate(`/rehearsal-room/w/${workspaceId}/work-documents/${d.wdoc_id}`); }}
                  data-testid={`import-preview-open-document-${d.wdoc_id}`}
                >
                  Open {members?.find((m) => m.member_id === d.owner_member_id)?.member_name || d.title}
                </Button>
              ))}
            </div>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="import-preview-close-after-publish">Close</Button>
          </div>
        ) : (
          <>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-ink-soft">Document title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={handleTitleBlur} data-testid="import-preview-title-input" />
            </div>

            <div className="flex items-center gap-3 flex-wrap text-xs">
              <Badge variant="outline" data-testid="import-preview-content-count">{draft.summary.content_block_count} static</Badge>
              <Badge variant="outline" data-testid="import-preview-input-count">{draft.summary.input_block_count} input</Badge>
              <span className="text-ink-soft">Workspace: {workspaceId}</span>
            </div>

            {draft.errors?.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="import-preview-blocking-errors">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">This document can't be published yet:</p>
                  <ul className="list-disc pl-4">{draft.errors.map((e, i) => <li key={i}>{e.message}</li>)}</ul>
                </div>
              </div>
            )}
            {draft.warnings?.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-mustard/40 bg-mustard/10 p-3 text-sm text-ink" data-testid="import-preview-warnings">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-mustard-dark" />
                <ul className="list-disc pl-4">{draft.warnings.map((w, i) => <li key={i}>{w.message}</li>)}</ul>
              </div>
            )}

            <div className="space-y-2" data-testid="import-preview-blocks-list">
              {blocks.map((block, i) => (
                <ImportPreviewBlockRow
                  key={block.import_key}
                  block={block}
                  index={i}
                  total={blocks.length}
                  onMove={handleMove}
                  onToggleExcluded={(key, excluded) => sendOverride(key, { excluded })}
                  onSaveOverride={sendOverride}
                />
              ))}
            </div>

            <div className="space-y-2 border-t border-ink/10 pt-3">
              <Label className="text-xs uppercase tracking-wide text-ink-soft">Personal or Shared</Label>
              <RadioGroup value={assignmentMode || ""} onValueChange={setAssignmentMode} className="flex gap-4">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="personal" id="assignment-personal" data-testid="import-preview-assignment-personal" />
                  <Label htmlFor="assignment-personal" className="text-sm cursor-pointer">Personal</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="shared" id="assignment-shared" data-testid="import-preview-assignment-shared" />
                  <Label htmlFor="assignment-shared" className="text-sm cursor-pointer">Shared</Label>
                </div>
              </RadioGroup>
              {assignmentMode === "personal" && (
                <div className="space-y-1.5 pl-1" data-testid="import-preview-member-picker">
                  {(members || []).map((m) => (
                    <div key={m.member_id} className="flex items-center gap-2">
                      <Checkbox checked={selectedMemberIds.includes(m.member_id)} onCheckedChange={() => toggleMember(m.member_id)} data-testid={`import-preview-member-${m.member_id}`} />
                      <span className="text-sm text-ink">{m.member_name}</span>
                    </div>
                  ))}
                  {selectedMemberIds.length > 1 && (
                    <p className="text-xs text-ink-soft" data-testid="import-preview-multi-member-note">
                      This will create {selectedMemberIds.length} separate personal documents — one per selected member, with no shared answers between them.
                    </p>
                  )}
                </div>
              )}
            </div>

            {publishError && (
              <p className="flex items-center gap-1.5 text-sm text-red-600" data-testid="import-preview-publish-error">
                <AlertTriangle size={14} /> {publishError}
              </p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="import-preview-close">Close</Button>
              <Button disabled={!canPublish || publish.isPending} onClick={handlePublish} data-testid="import-preview-confirm-publish">
                {publish.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Confirm &amp; Publish
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
